import React from "react";
import "./ChipGroup.scss";

export type ChipOption<V extends string = string> = {
  value: V;
  label: React.ReactNode;
  /** A colour dot before the label ("#rrggbb"); null shows an empty dot */
  swatch?: string | null;
  disabled?: boolean;
  /** Brand tooltip (data-tooltip), e.g. why it is disabled */
  tooltip?: string;
};

export type ChipGroupProps<V extends string = string> = {
  options: ChipOption<V>[];
  /** The picked values */
  value: V[];
  onChange: (value: V[]) => void;
  /** At most one pick: picking another replaces it */
  single?: boolean;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
};

/** Toggle chips: pick any number of values (sizes, colours, sports). Each chip is a button with aria-pressed. */
export function ChipGroup<V extends string = string>({
  options,
  value,
  onChange,
  single = false,
  ariaLabel,
  disabled = false,
  className,
}: ChipGroupProps<V>): React.JSX.Element {
  const picked = new Set(value);
  const toggle = (v: V) => {
    if (picked.has(v)) onChange(value.filter((x) => x !== v));
    else onChange(single ? [v] : [...value, v]);
  };
  return (
    <div className={["ui-chips", className].filter(Boolean).join(" ")} role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const on = picked.has(option.value);
        return (
          <button
            key={option.value}
            type="button"
            className={`ui-chip${on ? " ui-chip--on" : ""}`}
            aria-pressed={on}
            disabled={disabled || option.disabled}
            data-tooltip={option.tooltip}
            onClick={() => toggle(option.value)}
          >
            {option.swatch !== undefined ? (
              <span className="ui-chip__swatch" style={{ background: option.swatch || "transparent" }} aria-hidden="true" />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default ChipGroup;
